import {
  AuthenticatedConnectUser,
  CallbackMap,
  EventInfo,
  IntegrationInstallEvent,
  IntegrationUninstallEvent,
  ListenerFunction,
  PortalCloseEvent,
  PortalOpenEvent,
  SDKEventListenerMap,
  SDK_EVENT,
  WorkflowStateChangeEvent,
} from './types';

export default class SDKEventEmitter {
  private eventListenersMap: Partial<SDKEventListenerMap> = {};
  private integrationStateCallbacks: { [type: string]: CallbackMap } = {};

  /**
   * this will add listener to event listeners list
   * @param eventName
   * @returns function to unsubscribe current listener
   */
  subscribe(eventName: SDK_EVENT, listener: ListenerFunction): () => boolean {
    this.assertEventType(eventName);
    if (typeof listener !== 'function') {
      throw new Error(`no listener is provided for ${eventName}`);
    }

    this.eventListenersMap = {
      ...this.eventListenersMap,
      [eventName]: [...(this.eventListenersMap[eventName] ?? []), listener],
    };

    // return unsubscribe listener
    return () => {
      return this.unsubscribe(eventName, listener);
    };
  }

  /**
   * this method add bind callbackMap to integration type
   * and emit event for particular integration
   * @param integrationType
   * @param callbackMap
   */
  subscribeToIntegration(integrationType: string, callbackMap: CallbackMap): void {
    this.integrationStateCallbacks[integrationType] = { ...callbackMap };
  }

  /**
   * this method removes listener from evenListenerMap
   * @param eventName
   * @param listener
   * @returns true if successfully unsubscribed else false
   */
  unsubscribe(eventName: SDK_EVENT, listener: ListenerFunction): boolean {
    this.assertEventType(eventName);
    if (typeof listener !== 'function') {
      throw new Error('listener is required for unsubscription');
    }

    const oldListenersCount = (this.eventListenersMap[eventName] ?? []).length;

    this.eventListenersMap = {
      ...this.eventListenersMap,
      [eventName]: ((this.eventListenersMap[eventName] as ListenerFunction[]) ?? []).filter(
        (fn: ListenerFunction) => fn !== listener,
      ),
    };

    return oldListenersCount > this.eventListenersMap[eventName]!.length;
  }

  /**
   * check if event is valid paragon sdk event
   * @param eventName
   */
  private assertEventType(eventName: unknown): asserts eventName is SDK_EVENT {
    if (!Object.values(SDK_EVENT).includes(eventName as SDK_EVENT)) {
      throw new Error(`${eventName} is not valid paragon event`);
    }
  }

  /**
   * this method call attached listeners with payload for eventName type
   * @param eventName
   * @param eventPayload
   */
  private emitSafe(
    eventName: SDK_EVENT,
    ...eventPayload: Parameters<SDKEventListenerMap[keyof SDKEventListenerMap][number]>
  ): void {
    (this.eventListenersMap[eventName] ?? []).forEach((listener: Function) => {
      // sdk should not care about error inside listeners
      try {
        listener(...eventPayload);
      } catch (error) {
        console.error(error);
      }
    });
  }

  /**
   * this method will call errorCallback for integration
   * @param error
   * @param integrationType
   */
  emitError(error: Error, integrationType: string): void {
    this.invokeCallbackSafe(integrationType, 'onError', error);
  }

  /**
   *  this will call callBack function inside try-catch block
   * @param integrationType
   * @param callbackType
   * @param payload
   */
  private invokeCallbackSafe(
    integrationType: string,
    callbackType: keyof CallbackMap,
    // @ts-ignore could not infer optional callbacks
    ...payload: Parameters<CallbackMap[keyof CallbackMap]>
  ): void {
    try {
      //@ts-ignore TODO : check why this give type error
      this.integrationStateCallbacks[integrationType]?.[callbackType]?.(...payload);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * this will invoke emitSafe method with proper payload
   * @param event
   * @param userState
   * @param modalState
   */
  triggerEvent(
    { type, integrationId, integrationType, workflowId, workflowStateChange }: EventInfo,
    userState: AuthenticatedConnectUser,
  ): void {
    if (!integrationId || !integrationType) {
      return;
    }

    switch (type) {
      case SDK_EVENT.ON_INTEGRATION_INSTALL: {
        const eventPayload: [IntegrationInstallEvent, AuthenticatedConnectUser] = [
          {
            integrationId,
            integrationType,
          },
          userState,
        ];

        this.invokeCallbackSafe(integrationType, 'onInstall', ...eventPayload);
        this.emitSafe(SDK_EVENT.ON_INTEGRATION_INSTALL, ...eventPayload);
        break;
      }
      case SDK_EVENT.ON_INTEGRATION_UNINSTALL: {
        const eventPayload: [IntegrationUninstallEvent, AuthenticatedConnectUser] = [
          {
            integrationId,
            integrationType,
          },
          userState,
        ];

        this.invokeCallbackSafe(integrationType, 'onUninstall', ...eventPayload);
        this.emitSafe(SDK_EVENT.ON_INTEGRATION_UNINSTALL, ...eventPayload);
        break;
      }
      case SDK_EVENT.ON_PORTAL_OPEN: {
        const eventPayload: [PortalOpenEvent, AuthenticatedConnectUser] = [
          {
            integrationId,
            integrationType,
          },
          userState,
        ];

        this.invokeCallbackSafe(integrationType, 'onOpen', ...eventPayload);
        this.emitSafe(SDK_EVENT.ON_PORTAL_OPEN, ...eventPayload);
        break;
      }
      case SDK_EVENT.ON_PORTAL_CLOSE: {
        const eventPayload: [PortalCloseEvent, AuthenticatedConnectUser] = [
          {
            integrationId,
            integrationType,
          },
          userState,
        ];

        this.invokeCallbackSafe(integrationType, 'onClose', ...eventPayload);
        this.emitSafe(SDK_EVENT.ON_PORTAL_CLOSE, ...eventPayload);
        break;
      }
      case SDK_EVENT.ON_WORKFLOW_CHANGE: {
        const eventPayload: [WorkflowStateChangeEvent, AuthenticatedConnectUser] = [
          {
            integrationId,
            workflowId: workflowId as string,
            workflowStateChange: workflowStateChange || {},
          },
          userState,
        ];

        this.invokeCallbackSafe(integrationType, 'onWorkflowChange', ...eventPayload);
        this.emitSafe(SDK_EVENT.ON_WORKFLOW_CHANGE, ...eventPayload);
        break;
      }
      default:
        throw new Error(`${type} is not valid event.`);
    }
  }
}
